import { useV2TermsControllerGetCategories, type V2TermsCategoryResponseDto, V2TermsVersionCreateRequestDtoCategory as TermsCategory } from "@everon-it/api-client/contents-gw";
import { toEnumValue } from "@utils/typeGuards";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import useRouter from "@hooks/useRouter";
import useTermsDataStore from "@store/terms";
import { colors, typo } from "@styles/index";
import { useFormik } from "formik";
import React from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { termsData, type TermsInitialValues } from "./Data";

const TermsManagement: React.FC = () => {
    // Data
    const filter = termsData.forms;
    const formInitialValues = termsData.formInitialValues;
    const tableContentsInfoData = termsData.tableList;

    // Props
    const MAIN_TITLE = "약관 관리";
    const TABLE_TITLE = "약관 목록";
    const FILTER_GIRD_COLUMN = 2;

    // Hooks
    const DETAIL_PATH = PrivateRoutes.TermsDetail;

    const { push } = useRouter();
    const { setCategoryInfo } = useTermsDataStore();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL 파라미터에서 초기값 읽기
    const getInitialValues = (): TermsInitialValues => ({
        title: searchParams.get("title") ?? formInitialValues.title,
        isRequired: searchParams.get("isRequired") ?? formInitialValues.isRequired,
        isVisibleOnSignup: searchParams.get("isVisibleOnSignup") ?? formInitialValues.isVisibleOnSignup,
        isVisibleOnServicePage: searchParams.get("isVisibleOnServicePage") ?? formInitialValues.isVisibleOnServicePage,
    });

    // Formik
    const formik = useFormik<TermsInitialValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: values => {
            const filteredData: Record<string, string> = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value !== "") {
                    filteredData[key] = String(value);
                }
            });
            setSearchParams(filteredData);
        },
    });

    // 검색 필터 초기화
    const handleFormReset = () => {
        setSearchParams({});
        formik.resetForm();
    };

    // api-client: 약관 목록 조회
    const { data } = useV2TermsControllerGetCategories(
        {
            title: formik.values.title || undefined,
            isRequired: formik.values.isRequired ? formik.values.isRequired === "true" : undefined,
            isVisibleOnSignup: formik.values.isVisibleOnSignup ? formik.values.isVisibleOnSignup === "true" : undefined,
            isVisibleOnServicePage: formik.values.isVisibleOnServicePage ? formik.values.isVisibleOnServicePage === "true" : undefined,
        },
        {
            query: {
                retry: false,
                refetchOnWindowFocus: false,
            },
        },
    );

    const listData: V2TermsCategoryResponseDto[] = data?.rows ?? [];
    const totalCount = data?.count ?? 0;

    /** 상세 페이지 이동 */
    const handleDetailClick = (category: V2TermsCategoryResponseDto["category"]) => {
        const selectedItem = listData.find(item => item.category === category);
        if (selectedItem) {
            const validCategory = toEnumValue(selectedItem.category, TermsCategory);
            setCategoryInfo({
                ...selectedItem,
                category: validCategory,
            });
        }
        push(`${DETAIL_PATH}/${category}`);
    };

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>
            <ContentBox>
                <Filter formik={formik} filterData={filter} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>
            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                </TableHeader>
                <Table<V2TermsCategoryResponseDto> rowData={listData} tableContentsInfoData={tableContentsInfoData} currentPageSize={listData.length} onTermsDetailClick={handleDetailClick} />
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default TermsManagement;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;
